import React, { useState, useEffect } from 'react';
import { X, Upload, User, Shield, BookOpen } from 'lucide-react';

const ProfileModal = ({ isOpen, onClose, userProfile, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        avatar: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [uploadedImage, setUploadedImage] = useState('');

    useEffect(() => {
        if (userProfile) {
            setFormData({
                name: userProfile.name || '',
                email: userProfile.email || '',
                avatar: userProfile.avatar || '',
                newPassword: '',
                confirmPassword: ''
            });
            setUploadedImage(userProfile.avatar || '');
        }
    }, [userProfile, isOpen]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                alert('Saiz gambar terlalu besar. Maksimum 2MB.');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedImage(reader.result);
                setFormData({ ...formData, avatar: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setUploadedImage('');
        setFormData({ ...formData, avatar: '' });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Add avatar to the form data
        const formElement = e.target;
        const submitEvent = {
            preventDefault: () => {},
            target: {
                name: formElement.name,
                newPassword: formElement.newPassword,
                confirmPassword: formElement.confirmPassword,
                avatar: { value: formData.avatar }
            }
        };
        
        onSave(submitEvent);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h2 className="text-2xl font-bold text-slate-900">Profil Pengguna</h2>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
                    >
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                    <div className="space-y-6">
                        {/* Avatar Upload */}
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl font-bold overflow-hidden">
                                    {uploadedImage ? (
                                        <img src={uploadedImage} alt="Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        formData.name ? formData.name.charAt(0).toUpperCase() : <User size={32} />
                                    )}
                                </div>
                                {uploadedImage && (
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute -top-1 -right-1 w-7 h-7 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 transition-colors shadow-lg"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            
                            <div className="flex gap-2">
                                <label className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-semibold hover:bg-emerald-600 cursor-pointer transition-all flex items-center gap-2">
                                    <Upload size={16} />
                                    Muat Naik Gambar
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                </label>
                            </div>
                            <p className="text-xs text-slate-500 text-center">
                                Format: JPG, PNG, GIF (Maks 2MB)
                            </p>
                        </div>

                        {/* Basic Info */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                NAMA PENUH *
                            </label>
                            <input
                                type="text"
                                name="name"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                                placeholder="Nama Penuh"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                ALAMAT EMEL
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                disabled
                                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed outline-none"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                Emel tidak boleh diubah
                            </p>
                        </div>

                        {/* Password Section */}
                        <div className="pt-4 border-t border-slate-100">
                            <h3 className="font-bold text-slate-700 mb-4">Tukar Kata Laluan</h3>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                        KATA LALUAN BARU (OPTIONAL)
                                    </label>
                                    <p className="text-xs text-slate-500 mb-2">
                                        Biarkan kosong jika tidak mahu menukar kata laluan
                                    </p>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                                        SAHKAN KATA LALUAN
                                    </label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Access Level Info (Read-only) */}
                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-2 mb-2">
                                <Shield size={18} className="text-slate-500" />
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    TAHAP AKSES ANDA
                                </label>
                            </div>
                            <div className="font-semibold text-slate-700">
                                {userProfile?.isSuperAdmin ? 'Super Admin (Global)' : 'Pengguna Projek'}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                Anda tidak boleh mengubah tahap akses anda sendiri
                            </p>
                        </div>

                        {/* User Manual Button */}
                        <button
                            type="button"
                            data-testid="open-user-manual-btn"
                            onClick={() => window.open('/manual.html', '_blank')}
                            className="w-full flex items-center gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
                        >
                            <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                <BookOpen size={20} />
                            </div>
                            <div>
                                <div className="font-semibold text-emerald-800 text-sm">Panduan Pengguna</div>
                                <p className="text-xs text-emerald-600 mt-0.5">Buka manual lengkap penggunaan sistem (boleh cetak/simpan PDF)</p>
                            </div>
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 mt-8 pt-6 border-t border-slate-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                        >
                            Batal
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 shadow-lg shadow-emerald-600/30 transition-all"
                        >
                            Simpan Perubahan
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default React.memo(ProfileModal);
